import { c as _c } from "react/compiler-runtime";
import { usePageView } from '@/analytics/pageTypes';
import { Layout } from '@/components/layout';
import { Link } from '@/components/links';
import { StackCards } from '@/components/StackCards';
import { Card } from '@/components/StackCards/types';
import IconAdjustments from '@/assets/icons/heroIcons/Adjustments.svg';
import IconCog from '@/assets/icons/heroIcons/Cog.svg';
import IconDeferredPayment from '@/assets/icons/heroIcons/Deferred-payment.svg';
import IconThird from '@/assets/icons/heroIcons/Icon-third.svg';
import IconLoan from '@/assets/icons/heroIcons/Loan.svg';
import IconCard from '@/assets/icons/heroIcons/Card-front.svg';
import IconShoppingCart from '@/assets/icons/heroIcons/Shopping-cart.svg';
import Hero from '@/sections/Hero';
import MoreInfoSection from '@/sections/MoreInfo';
import { FaqIntro } from '@/sections/FaqIntro';
import { Button } from '@/components/buttons';
import { InfoBoxes } from '@/components/boxes';
import { SectionDelimiter } from '@/components/SectionDelimiter';
import { SectionHeadline } from '@/components/SectionHeadline';
import { ContentContainer } from '@/components/layout';
import { Notice } from '@/sections/investment/Notice';
import { type IntlProps, getIntlProps } from '@/utils/intl';
import type { GetStaticProps } from 'next';
import { Trans, useTranslation } from 'react-i18next';
import { LINKS } from '@/constants/links';
import React from 'react';
import { mergeClasses } from '@/utils/style';
import { NumericRange } from '@/types/range';
import Image from 'next/image';
import { getPageBannerBySlug, PageBanner } from '@/api/sanity/pageBanners';
import { SeoHeader } from '@/components/SeoHeader';
import { Text } from '@/components/typography';
import { isAllowedRoute } from '@/lang/pagesConfig';
import { getAllPageMenuPromoBanners, PageMenuPromoBanner } from '@/api/sanity/pageMenuPromoBanner';
import IconMaxi from '@/assets/icons/heroIcons/Maxi.svg';
import { getSectionAnchorId } from '@/utils/anchors';
interface Props extends IntlProps {
  pageBanner: PageBanner | null;
  menuPromoBanners?: PageMenuPromoBanner[];
}
export default function Page(props) {
  const $ = _c(193);
  usePageView("investment");
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["investment", "core"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation(t0);
  const {
    pageBanner,
    menuPromoBanners
  } = props;
  let t1;
  if ($[1] !== t) {
    t1 = Array.from({
      length: 5
    }, (_, index) => {
      const cardIndex = index + 1 as NumericRange<1, 5>;
      return {
        index,
        title: <div className={mergeClasses(cardIndex === 5 && "text-center lg:text-left")}>{t(`howItWorks.items.${cardIndex}.title`)}</div>,
        ...(cardIndex < 5 ? {
          description: cardIndex === 1 ? <Trans t={t} i18nKey="investment:howItWorks.items.1.text"><Link className="lg:text-xl" to={LINKS().revolving} testId="investmentToMaxi" analyticsParam="investmentToMaxi" /></Trans> : t(`howItWorks.items.${cardIndex}.text`)
        } : {
          footer: <Button analyticsParam="investmentHowItWorksiWantMaxi" testId="investmentHowItWorksiWantMaxi" variant="primary" size="lg" className="m-auto w-full md:w-auto lg:m-0" to={LINKS().registration}>{t(`howItWorks.items.${cardIndex}.text`)}</Button>
        }),
        images: {
          desktop: {
            src: `/images/sections/investment/cards/cs/${cardIndex}.png`,
            alt: t(`howItWorks.items.${cardIndex}.title`)
          },
          mobile: {
            src: `/images/sections/investment/cards/cs/${cardIndex}.png`,
            alt: t(`howItWorks.items.${cardIndex}.title`)
          },
          ...(cardIndex < 5 && {
            variant: "appMockup"
          })
        }
      };
    });
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const cards = t1;
  let t2;
  if ($[3] !== t) {
    t2 = t("seo.title");
    $[3] = t;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t) {
    t3 = t("seo.description");
    $[5] = t;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t2 || $[8] !== t3) {
    t4 = <SeoHeader title={t2} description={t3} />;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const t5 = pageBanner?.videoId;
  let t6;
  if ($[10] !== pageBanner?.title || $[11] !== t) {
    t6 = pageBanner?.title ?? t("banner.title");
    $[10] = pageBanner?.title;
    $[11] = t;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== pageBanner?.text || $[14] !== t) {
    t7 = pageBanner?.text ?? t("banner.text");
    $[13] = pageBanner?.text;
    $[14] = t;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== pageBanner || $[17] !== t) {
    t8 = pageBanner ? pageBanner.buttons : [{
      ariaLabel: t("banner.cta"),
      label: t("banner.cta"),
      to: LINKS().registration,
      testId: "investmentHeroPrimaryCTA",
      analyticsParam: "investmentHeroPrimaryCTA",
      variant: "primary"
    }];
    $[16] = pageBanner;
    $[17] = t;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const t9 = pageBanner?.image?.url ?? "/images/sections/investment/hero/cs/hero.png";
  let t10;
  if ($[19] !== pageBanner?.image?.alt || $[20] !== t) {
    t10 = pageBanner?.image?.alt ?? t("banner.title");
    $[19] = pageBanner?.image?.alt;
    $[20] = t;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== t10 || $[23] !== t9) {
    t11 = {
      url: t9,
      alt: t10
    };
    $[22] = t10;
    $[23] = t9;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  const t12 = pageBanner?.variant || "mockupMobile";
  let t13;
  if ($[25] !== t11 || $[26] !== t12 || $[27] !== t5 || $[28] !== t6 || $[29] !== t7 || $[30] !== t8) {
    t13 = <Hero videoId={t5} title={t6} text={t7} buttons={t8} image={t11} variant={t12} />;
    $[25] = t11;
    $[26] = t12;
    $[27] = t5;
    $[28] = t6;
    $[29] = t7;
    $[30] = t8;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <div className="bg-blue-50"><SectionDelimiter color="bg-blue-100" background="bg-transparent" /></div>;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = getSectionAnchorId("advantages.title");
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== t) {
    t16 = t("advantages.title");
    $[34] = t;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== t16) {
    t17 = <SectionHeadline className="mb-10 lg:mb-16" anchorKey="advantages.title">{t16}</SectionHeadline>;
    $[36] = t16;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  if ($[38] !== t) {
    t18 = t("advantages.items.1.title");
    $[38] = t;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  let t19;
  if ($[40] !== t) {
    t19 = t("advantages.items.1.text");
    $[40] = t;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  let t20;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <IconCog />;
    $[42] = t20;
  } else {
    t20 = $[42];
  }
  let t21;
  if ($[43] !== t18 || $[44] !== t19) {
    t21 = {
      title: t18,
      paragraph: t19,
      icon: t20
    };
    $[43] = t18;
    $[44] = t19;
    $[45] = t21;
  } else {
    t21 = $[45];
  }
  let t22;
  if ($[46] !== t) {
    t22 = t("advantages.items.2.title");
    $[46] = t;
    $[47] = t22;
  } else {
    t22 = $[47];
  }
  let t23;
  if ($[48] !== t) {
    t23 = t("advantages.items.2.text");
    $[48] = t;
    $[49] = t23;
  } else {
    t23 = $[49];
  }
  let t24;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <IconShoppingCart />;
    $[50] = t24;
  } else {
    t24 = $[50];
  }
  let t25;
  if ($[51] !== t22 || $[52] !== t23) {
    t25 = {
      title: t22,
      paragraph: t23,
      icon: t24
    };
    $[51] = t22;
    $[52] = t23;
    $[53] = t25;
  } else {
    t25 = $[53];
  }
  let t26;
  if ($[54] !== t) {
    t26 = t("advantages.items.3.title");
    $[54] = t;
    $[55] = t26;
  } else {
    t26 = $[55];
  }
  let t27;
  if ($[56] !== t) {
    t27 = t("advantages.items.3.text");
    $[56] = t;
    $[57] = t27;
  } else {
    t27 = $[57];
  }
  let t28;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <IconLoan />;
    $[58] = t28;
  } else {
    t28 = $[58];
  }
  let t29;
  if ($[59] !== t26 || $[60] !== t27) {
    t29 = {
      title: t26,
      paragraph: t27,
      icon: t28
    };
    $[59] = t26;
    $[60] = t27;
    $[61] = t29;
  } else {
    t29 = $[61];
  }
  let t30;
  if ($[62] !== t) {
    t30 = t("advantages.items.4.title");
    $[62] = t;
    $[63] = t30;
  } else {
    t30 = $[63];
  }
  let t31;
  if ($[64] !== t) {
    t31 = t("advantages.items.4.text");
    $[64] = t;
    $[65] = t31;
  } else {
    t31 = $[65];
  }
  let t32;
  if ($[66] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <IconAdjustments />;
    $[66] = t32;
  } else {
    t32 = $[66];
  }
  let t33;
  if ($[67] !== t30 || $[68] !== t31) {
    t33 = {
      title: t30,
      paragraph: t31,
      icon: t32
    };
    $[67] = t30;
    $[68] = t31;
    $[69] = t33;
  } else {
    t33 = $[69];
  }
  let t34;
  if ($[70] !== t21 || $[71] !== t25 || $[72] !== t29 || $[73] !== t33) {
    t34 = <InfoBoxes items={[t21, t25, t29, t33]} variant="basic" />;
    $[70] = t21;
    $[71] = t25;
    $[72] = t29;
    $[73] = t33;
    $[74] = t34;
  } else {
    t34 = $[74];
  }
  let t35;
  if ($[75] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = <Link testId="investmentRevolving" analyticsParam="investmentRevolving" to={LINKS().revolving} />;
    $[75] = t35;
  } else {
    t35 = $[75];
  }
  let t36;
  if ($[76] !== t) {
    t36 = <Text as="p" variant="p4" className="mx-auto my-10 max-w-142 text-center text-gray-600 lg:my-12"><Trans t={t} i18nKey="advantages.text">{t35}</Trans></Text>;
    $[76] = t;
    $[77] = t36;
  } else {
    t36 = $[77];
  }
  let t37;
  if ($[78] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = LINKS();
    $[78] = t37;
  } else {
    t37 = $[78];
  }
  const t38 = t37.registration;
  let t39;
  if ($[79] !== t) {
    t39 = t("advantages.cta");
    $[79] = t;
    $[80] = t39;
  } else {
    t39 = $[80];
  }
  let t40;
  if ($[81] !== t39) {
    t40 = <Button to={t38} testId="investmentRegistration" analyticsParam="investmentRegistration" size="lg" variant="primary" align="center">{t39}</Button>;
    $[81] = t39;
    $[82] = t40;
  } else {
    t40 = $[82];
  }
  let t41;
  if ($[83] !== t17 || $[84] !== t34 || $[85] !== t36 || $[86] !== t40) {
    t41 = <div className="relative z-10 mx-auto max-w-[48rem]">{t17}{t34}{t36}{t40}</div>;
    $[83] = t17;
    $[84] = t34;
    $[85] = t36;
    $[86] = t40;
    $[87] = t41;
  } else {
    t41 = $[87];
  }
  let t42;
  if ($[88] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = <div className="bgCircle absolute top-0 left-0 -translate-x-1/4" />;
    $[88] = t42;
  } else {
    t42 = $[88];
  }
  let t43;
  if ($[89] !== t41) {
    t43 = <div className="relative z-30 bg-blue-100 px-4 py-10 lg:py-20" id={t15}>{t41}{t42}</div>;
    $[89] = t41;
    $[90] = t43;
  } else {
    t43 = $[90];
  }
  let t44;
  if ($[91] === Symbol.for("react.memo_cache_sentinel")) {
    t44 = getSectionAnchorId("howItWorks.title");
    $[91] = t44;
  } else {
    t44 = $[91];
  }
  let t45;
  if ($[92] !== t) {
    t45 = t("howItWorks.title");
    $[92] = t;
    $[93] = t45;
  } else {
    t45 = $[93];
  }
  let t46;
  if ($[94] !== t45) {
    t46 = <SectionHeadline className="mb-10 px-4 md:mb-16" anchorKey="howItWorks.title">{t45}</SectionHeadline>;
    $[94] = t45;
    $[95] = t46;
  } else {
    t46 = $[95];
  }
  let t47;
  if ($[96] !== cards) {
    t47 = <StackCards imagePosition="left" cards={cards} />;
    $[96] = cards;
    $[97] = t47;
  } else {
    t47 = $[97];
  }
  let t48;
  if ($[98] !== t46 || $[99] !== t47) {
    t48 = <div className="relative bg-blue-100 pt-10 pb-14 lg:pt-20 lg:pb-32" id={t44}>{t46}{t47}</div>;
    $[98] = t46;
    $[99] = t47;
    $[100] = t48;
  } else {
    t48 = $[100];
  }
  let t49;
  if ($[101] === Symbol.for("react.memo_cache_sentinel")) {
    t49 = <SectionDelimiter color="bg-blue-50" background="bg-blue-100" />;
    $[101] = t49;
  } else {
    t49 = $[101];
  }
  let t50;
  if ($[102] === Symbol.for("react.memo_cache_sentinel")) {
    t50 = getSectionAnchorId("investment.fees.title");
    $[102] = t50;
  } else {
    t50 = $[102];
  }
  let t51;
  if ($[103] !== t) {
    t51 = t("fees.title");
    $[103] = t;
    $[104] = t51;
  } else {
    t51 = $[104];
  }
  let t52;
  if ($[105] !== t51) {
    t52 = <div className="mx-auto w-full max-w-[50rem] text-center"><SectionHeadline className="mb-4 px-0" anchorKey="investment.fees.title">{t51}</SectionHeadline></div>;
    $[105] = t51;
    $[106] = t52;
  } else {
    t52 = $[106];
  }
  let t53;
  if ($[107] !== t) {
    t53 = <div className="flex flex-col gap-4">{Array.from({
        length: 6
      }, (__0, index_0) => {
        const itemIndex = index_0 + 1 as NumericRange<1, 5>;
        return <div className="flex flex-row justify-between gap-4 align-top" key={itemIndex}><Text variant="p2" className="text-left">{t(`fees.items.${itemIndex}.title`)}</Text><Text variant="p2" weight="semibold" className="text-right">{t(`fees.items.${itemIndex}.text`)}</Text></div>;
      })}</div>;
    $[107] = t;
    $[108] = t53;
  } else {
    t53 = $[108];
  }
  let t54;
  if ($[109] === Symbol.for("react.memo_cache_sentinel")) {
    t54 = LINKS();
    $[109] = t54;
  } else {
    t54 = $[109];
  }
  const t55 = t54.registration;
  let t56;
  if ($[110] !== t) {
    t56 = t("fees.cta");
    $[110] = t;
    $[111] = t56;
  } else {
    t56 = $[111];
  }
  let t57;
  if ($[112] !== t56) {
    t57 = <Button to={t55} testId="investmentRevolving" analyticsParam="investmentRevolving" size="lg" variant="primary" align="center" className="my-10 lg:my-12">{t56}</Button>;
    $[112] = t56;
    $[113] = t57;
  } else {
    t57 = $[113];
  }
  let t58;
  if ($[114] === Symbol.for("react.memo_cache_sentinel")) {
    t58 = <Link testId="investmentIndigo" analyticsParam="investmentIndigo" to="https://indigo.patria.cz/" target="_blank" />;
    $[114] = t58;
  } else {
    t58 = $[114];
  }
  let t59;
  if ($[115] === Symbol.for("react.memo_cache_sentinel")) {
    t59 = <Link testId="investmentRegistration" analyticsParam="investmentRegistration" to={LINKS().registration} />;
    $[115] = t59;
  } else {
    t59 = $[115];
  }
  let t60;
  if ($[116] !== t) {
    t60 = <Text variant="p3" className="mx-auto max-w-142 text-center lg:my-14"><Trans t={t} i18nKey="fees.text">{t58}{t59}</Trans></Text>;
    $[116] = t;
    $[117] = t60;
  } else {
    t60 = $[117];
  }
  let t61;
  if ($[118] !== t53 || $[119] !== t57 || $[120] !== t60) {
    t61 = <div className="mx-auto my-8 max-w-[35.5rem] md:my-16">{t53}{t57}{t60}</div>;
    $[118] = t53;
    $[119] = t57;
    $[120] = t60;
    $[121] = t61;
  } else {
    t61 = $[121];
  }
  let t62;
  if ($[122] === Symbol.for("react.memo_cache_sentinel")) {
    t62 = <Notice />;
    $[122] = t62;
  } else {
    t62 = $[122];
  }
  let t63;
  if ($[123] !== t) {
    t63 = t("whoWillHelpYou.title");
    $[123] = t;
    $[124] = t63;
  } else {
    t63 = $[124];
  }
  let t64;
  if ($[125] !== t63) {
    t64 = <div className="w-full"><Image src="/images/sections/investment/cs/investment.png" alt={t63} width={564} height={510} className="mx-auto max-w-[90%] md:max-w-full" /></div>;
    $[125] = t63;
    $[126] = t64;
  } else {
    t64 = $[126];
  }
  let t65;
  if ($[127] !== t) {
    t65 = t("whoWillHelpYou.title");
    $[127] = t;
    $[128] = t65;
  } else {
    t65 = $[128];
  }
  let t66;
  if ($[129] !== t65) {
    t66 = <SectionHeadline className="mb-5 px-0 lg:text-left" anchorKey="whoWillHelpYou.title">{t65}</SectionHeadline>;
    $[129] = t65;
    $[130] = t66;
  } else {
    t66 = $[130];
  }
  let t67;
  if ($[131] !== t) {
    t67 = t("whoWillHelpYou.text");
    $[131] = t;
    $[132] = t67;
  } else {
    t67 = $[132];
  }
  let t68;
  if ($[133] !== t67) {
    t68 = <Text variant="p2" className="text-center text-gray-800 lg:text-left">{t67}</Text>;
    $[133] = t67;
    $[134] = t68;
  } else {
    t68 = $[134];
  }
  let t69;
  if ($[135] !== t66 || $[136] !== t68) {
    t69 = <div className="w-full">{t66}{t68}</div>;
    $[135] = t66;
    $[136] = t68;
    $[137] = t69;
  } else {
    t69 = $[137];
  }
  let t70;
  if ($[138] !== t64 || $[139] !== t69) {
    t70 = <div className="relative z-10"><div className="pt-20 lg:pt-40"><ContentContainer><div className="flex flex-col items-center gap-12 lg:flex-row lg:gap-32">{t64}{t69}</div></ContentContainer></div></div>;
    $[138] = t64;
    $[139] = t69;
    $[140] = t70;
  } else {
    t70 = $[140];
  }
  let t71;
  if ($[141] !== t52 || $[142] !== t61 || $[143] !== t70) {
    t71 = <div className="relative bg-blue-50 px-4 pt-10 pb-14 lg:pt-20 lg:pb-32" id={t50}>{t52}{t61}{t62}{t70}</div>;
    $[141] = t52;
    $[142] = t61;
    $[143] = t70;
    $[144] = t71;
  } else {
    t71 = $[144];
  }
  let t72;
  if ($[145] === Symbol.for("react.memo_cache_sentinel")) {
    t72 = <SectionDelimiter color="bg-blue-100" background="bg-blue-50" />;
    $[145] = t72;
  } else {
    t72 = $[145];
  }
  let t73;
  if ($[146] === Symbol.for("react.memo_cache_sentinel")) {
    t73 = LINKS();
    $[146] = t73;
  } else {
    t73 = $[146];
  }
  let t74;
  if ($[147] === Symbol.for("react.memo_cache_sentinel")) {
    t74 = <Link to="/caste-dotazy/investice" analyticsParam="faqInvestments" testId="faqInvestments" />;
    $[147] = t74;
  } else {
    t74 = $[147];
  }
  let t75;
  if ($[148] !== t) {
    t75 = <div className="py-10 lg:py-20"><FaqIntro link={t73.faq} text={<Trans i18nKey="faq.text" t={t}>{t74}</Trans>} /></div>;
    $[148] = t;
    $[149] = t75;
  } else {
    t75 = $[149];
  }
  let t76;
  if ($[150] === Symbol.for("react.memo_cache_sentinel")) {
    t76 = <IconDeferredPayment className="fillButtonIcon" />;
    $[150] = t76;
  } else {
    t76 = $[150];
  }
  let t77;
  if ($[151] !== t) {
    t77 = t("core:link.deferredPayment");
    $[151] = t;
    $[152] = t77;
  } else {
    t77 = $[152];
  }
  let t78;
  if ($[153] === Symbol.for("react.memo_cache_sentinel")) {
    t78 = LINKS();
    $[153] = t78;
  } else {
    t78 = $[153];
  }
  let t79;
  if ($[154] !== t77) {
    t79 = {
      analyticsParam: "investmentMoreInfoDeferredPayment",
      icon: t76,
      testId: "investmentMoreInfoDeferredPayment",
      title: t77,
      to: t78.deferredPayment
    };
    $[154] = t77;
    $[155] = t79;
  } else {
    t79 = $[155];
  }
  let t80;
  if ($[156] === Symbol.for("react.memo_cache_sentinel")) {
    t80 = <IconMaxi className="fillButtonIcon" />;
    $[156] = t80;
  } else {
    t80 = $[156];
  }
  let t81;
  if ($[157] !== t) {
    t81 = t("core:products.revolving");
    $[157] = t;
    $[158] = t81;
  } else {
    t81 = $[158];
  }
  let t82;
  if ($[159] === Symbol.for("react.memo_cache_sentinel")) {
    t82 = LINKS();
    $[159] = t82;
  } else {
    t82 = $[159];
  }
  let t83;
  if ($[160] !== t81) {
    t83 = {
      analyticsParam: "investmentMoreInfoRevolvingApplication",
      icon: t80,
      testId: "investmentMoreInfoRevolvingApplication",
      title: t81,
      to: t82.revolving
    };
    $[160] = t81;
    $[161] = t83;
  } else {
    t83 = $[161];
  }
  let t84;
  if ($[162] === Symbol.for("react.memo_cache_sentinel")) {
    t84 = <IconThird className="fillButtonIcon" />;
    $[162] = t84;
  } else {
    t84 = $[162];
  }
  let t85;
  if ($[163] !== t) {
    t85 = t("core:products.payInThree");
    $[163] = t;
    $[164] = t85;
  } else {
    t85 = $[164];
  }
  let t86;
  if ($[165] === Symbol.for("react.memo_cache_sentinel")) {
    t86 = LINKS();
    $[165] = t86;
  } else {
    t86 = $[165];
  }
  let t87;
  if ($[166] !== t85) {
    t87 = {
      analyticsParam: "investmentMoreInfoPayInThree",
      icon: t84,
      testId: "investmentMoreInfoPayInThree",
      title: t85,
      to: t86.payInThree
    };
    $[166] = t85;
    $[167] = t87;
  } else {
    t87 = $[167];
  }
  let t88;
  if ($[168] === Symbol.for("react.memo_cache_sentinel")) {
    t88 = <IconCard className="fillButtonIcon" />;
    $[168] = t88;
  } else {
    t88 = $[168];
  }
  let t89;
  if ($[169] !== t) {
    t89 = t("core:products.card");
    $[169] = t;
    $[170] = t89;
  } else {
    t89 = $[170];
  }
  let t90;
  if ($[171] === Symbol.for("react.memo_cache_sentinel")) {
    t90 = LINKS();
    $[171] = t90;
  } else {
    t90 = $[171];
  }
  let t91;
  if ($[172] !== t89) {
    t91 = {
      analyticsParam: "investmentMoreInfoCard",
      icon: t88,
      testId: "investmentMoreInfoCard",
      title: t89,
      to: t90.card
    };
    $[172] = t89;
    $[173] = t91;
  } else {
    t91 = $[173];
  }
  let t92;
  if ($[174] !== t79 || $[175] !== t83 || $[176] !== t87 || $[177] !== t91) {
    t92 = <MoreInfoSection buttons={[t79, t83, t87, t91]} />;
    $[174] = t79;
    $[175] = t83;
    $[176] = t87;
    $[177] = t91;
    $[178] = t92;
  } else {
    t92 = $[178];
  }
  let t93;
  if ($[179] !== t75 || $[180] !== t92) {
    t93 = <div className="relative z-10">{t75}{t92}</div>;
    $[179] = t75;
    $[180] = t92;
    $[181] = t93;
  } else {
    t93 = $[181];
  }
  let t94;
  if ($[182] === Symbol.for("react.memo_cache_sentinel")) {
    t94 = <div className="bgCircle absolute right-0 bottom-0 translate-x-1/4 translate-y-1/4 rotate-180" />;
    $[182] = t94;
  } else {
    t94 = $[182];
  }
  let t95;
  if ($[183] !== t93) {
    t95 = <div className="relative overflow-hidden bg-blue-100">{t93}{t94}</div>;
    $[183] = t93;
    $[184] = t95;
  } else {
    t95 = $[184];
  }
  let t96;
  if ($[185] !== menuPromoBanners || $[186] !== t13 || $[187] !== t4 || $[188] !== t43 || $[189] !== t48 || $[190] !== t71 || $[191] !== t95) {
    t96 = <Layout menuPromoBanners={menuPromoBanners}>{t4}{t13}{t14}{t43}{t48}{t49}{t71}{t72}{t95}</Layout>;
    $[185] = menuPromoBanners;
    $[186] = t13;
    $[187] = t4;
    $[188] = t43;
    $[189] = t48;
    $[190] = t71;
    $[191] = t95;
    $[192] = t96;
  } else {
    t96 = $[192];
  }
  return t96;
}
export const getStaticProps = (async ctx => {
  const isAllowed = isAllowedRoute('investuj-pri-nakupovani');
  if (!isAllowed) {
    return {
      notFound: true
    };
  }
  return {
    props: {
      pageBanner: await getPageBannerBySlug('investment'),
      menuPromoBanners: await getAllPageMenuPromoBanners(),
      ...(await getIntlProps(ctx, ['core', 'investment']))
    }
  };
}) satisfies GetStaticProps<Props>;